import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  disconnectStripe,
  getPaymentUrl,
  getStripeConnectionStatus,
  listInvoicePayments,
  refreshStripeConnection,
  startStripeOnboarding,
} from '@/requests/stripe'

export function useStripeConnection(enabled = true) {
  return useQuery({
    queryKey: ['stripe', 'connection'],
    queryFn: getStripeConnectionStatus,
    enabled,
  })
}

export function useStartStripeOnboardingMutation() {
  return useMutation({
    mutationFn: async () => {
      const result = await startStripeOnboarding()
      if (result.error) throw new Error(result.error.message)
      return result
    },
  })
}

export function useRefreshStripeConnectionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await refreshStripeConnection()
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stripe', 'connection'] })
    },
  })
}

export function useDisconnectStripeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await disconnectStripe()
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stripe', 'connection'] })
    },
  })
}

export function useGetPaymentUrlMutation() {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const result = await getPaymentUrl(invoiceId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
  })
}

export function useInvoicePayments(invoiceId: string) {
  return useQuery({
    queryKey: ['stripe', 'payments', invoiceId],
    queryFn: () => listInvoicePayments(invoiceId),
    enabled: !!invoiceId,
  })
}
